// What the Home takes as a new project and the Page pane as a Page Reference: files picked
// together and told apart by their extension (docs/design/pdf-sourcing.md, decisions 1 and 9).
// The engine refuses a Document that does not name its file's SHA-256 too; the page says so at
// the pick, before the create form is filled.

import type { PickRefusal } from './home.ts'

export type SourcePick<F> =
  | { kind: 'docx'; source: F }
  | { kind: 'pdf'; source: F; structure: F }
  | { kind: 'refused'; refusal: PickRefusal }

export type ReferencePick<F> = { kind: 'pdf'; pdf: F } | { kind: 'reference'; pdf: F; placed: F } | { kind: 'refused'; reason: string }

const extension = (name: string) => /\.([^.]+)$/.exec(name.toLowerCase())?.[1] ?? ''

function sorted<F extends { name: string }>(files: F[]) {
  const of = (ext: string) => files.filter(f => extension(f.name) === ext)
  return { docx: of('docx'), pdf: of('pdf'), json: of('json') }
}

/// One DOCX, or one PDF with one Document JSON, picked together in either order.
export function sourcePick<F extends { name: string }>(files: F[]): SourcePick<F> {
  const { docx, pdf, json } = sorted(files)
  if (files.length === 1 && docx[0]) return { kind: 'docx', source: docx[0] }
  if (files.length === 2 && pdf[0] && json[0]) return { kind: 'pdf', source: pdf[0], structure: json[0] }
  if (files.length === 1 && pdf[0]) return { kind: 'refused', refusal: { kind: 'pdf-alone', pdf: pdf[0].name } }
  if (files.length === 1 && json[0]) return { kind: 'refused', refusal: { kind: 'companion-alone', json: json[0].name } }
  return { kind: 'refused', refusal: { kind: 'not-a-source', files: files.length } }
}

export type HomeDrop = { kind: 'bundle' } | { kind: 'source' } | { kind: 'refused'; refusal: PickRefusal }

/// What the Home takes as a drop (docs/design/ux-review.md, unit 17), by the names alone: one
/// bundle, or files for a new project, which `sourcePick` then judges.
export function homeDrop(names: string[]): HomeDrop {
  const zips = names.filter(n => extension(n) === 'zip')
  if (zips.length === 1 && names.length === 1) return { kind: 'bundle' }
  if (zips.length) return { kind: 'refused', refusal: { kind: 'bundle-not-alone' } }
  if (names.length) return { kind: 'source' }
  return { kind: 'refused', refusal: { kind: 'nothing' } }
}

/// What the drop zone says it takes.
export const dropWords = (native: boolean): string => (native ? 'a DOCX, a PDF, or a bundle' : 'a DOCX, a PDF with its JSON, or a bundle')

/// Whether a refused pick involved a PDF or a JSON, so the refusal can say how the companion
/// file is made.
export const needsCompanion = (names: string[]): boolean => names.some(n => extension(n) === 'pdf' || extension(n) === 'json')

/// How the companion file is obtained, one visible step from the pair refusal.
export function companionHelp(native: boolean): string {
  if (native) return 'This app reads a PDF itself and needs no companion file: pick the PDF alone. A JSON picked beside a PDF must be the Document JSON interplae-pdf adapt wrote for that very PDF, since the JSON names the PDF\'s SHA-256.'
  return 'The companion file is the Document JSON that the Interplae command line writes for a PDF. On a machine with the command line, run interplae-pdf adapt paper.pdf --out paper.json, which reads the pages and recognises their layout; a long paper takes a minute or two. Keep the PDF as it was, since the JSON names its SHA-256 and a changed PDF is refused. Then pick the PDF and the JSON together, or drop both here.'
}

/// Word's PDF of a DOCX alone, which is placed where PDFs are read, or with the placed Document
/// JSON `interplae-pdf place` wrote for it, a developer's path the page does not offer.
export function referencePick<F extends { name: string }>(files: F[]): ReferencePick<F> {
  const { pdf, json } = sorted(files)
  if (files.length === 1 && pdf[0]) return { kind: 'pdf', pdf: pdf[0] }
  if (files.length === 2 && pdf[0] && json[0]) return { kind: 'reference', pdf: pdf[0], placed: json[0] }
  return { kind: 'refused', reason: 'Pick the PDF Word saves of this DOCX.' }
}

/// The SHA-256 a Document JSON names for the file it was read from, as hex; null for text that
/// is not a Document JSON.
export function namedDigest(structure: string): string | null {
  let source: unknown
  try {
    source = (JSON.parse(structure) as { provenance?: { source?: unknown } } | null)?.provenance?.source
  } catch {
    return null
  }
  const bytes = Array.isArray(source) && source.length === 32 && source.every(n => Number.isInteger(n) && n >= 0 && n < 256) ? (source as number[]) : null
  return bytes && bytes.map(n => n.toString(16).padStart(2, '0')).join('')
}

/// Why a PDF and a Document JSON picked together are not a pair, or null when the JSON names the
/// PDF's SHA-256 (`digest`, hex).
export function pairRefusal(pdf: string, json: string, digest: string, structure: string): PickRefusal | null {
  const named = namedDigest(structure)
  if (named === null) return { kind: 'not-a-companion', json }
  return named === digest ? null : { kind: 'other-pdf', pdf, json }
}

/// The SHA-256 of `bytes` as hex.
export async function sha256(bytes: ArrayBuffer): Promise<string> {
  return [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map(b => b.toString(16).padStart(2, '0')).join('')
}
